import { Temporal } from "temporal-polyfill";

const localTimeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone;

const isValid = (value: Date | string | number): boolean => !isNaN(new Date(value as string).getTime());

const zoned = (value: Date | string | number): Temporal.ZonedDateTime | null =>
  isValid(value)
    ? Temporal.Instant.fromEpochMilliseconds(new Date(value as string).getTime()).toZonedDateTimeISO(localTimeZone)
    : null;

const invalidDate = (): Date => new Date(NaN);

/**
 * Formats a date and time to a localized string ("18/08/2026 14:30").
 *
 * @param value - The date to format. Can be a Date object, a date string, or a timestamp.
 * @returns The formatted date and time string, or "" when the value is empty.
 */
export function formatDateTime(value: Date | string | number): string {
  if (!value) return "";
  return new Date(value as string).toLocaleString("pt-BR", {
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/**
 * Converts a given date to ISO format (YYYY-MM-DD) in the local timezone.
 *
 * @param date - The date to convert.
 * @returns The local calendar date as "YYYY-MM-DD", or "" when the value is not a valid date.
 */
export function localDateISO(date: Date): string {
  return zoned(date)?.toPlainDate().toString() ?? "";
}

/**
 * Formats a date as ISO 8601 preserving the local UTC offset ("2026-08-18T14:30:00.000-03:00").
 * Unlike `Date.toISOString()`, the wall-clock time is kept and the offset written explicitly.
 *
 * @param value - The date to format. Can be a Date object, a date string, or a timestamp.
 * @returns The ISO 8601 string with local offset, or "" when the value is not a valid date.
 */
export function localISODateTime(value: Date | string | number): string {
  return zoned(value)?.toString({ timeZoneName: "never", fractionalSecondDigits: 3 }) ?? "";
}

/**
 * Returns a new Date at the last millisecond of the given date's local day (23:59:59.999).
 *
 * @param value - The date. Can be a Date object, a date string, or a timestamp.
 * @returns A new Date at the end of that local day, or an invalid Date when the value is not a valid date.
 */
export function endOfDay(value: Date | string | number): Date {
  return setTime(value, { hour: 23, minute: 59, second: 59, millisecond: 999 });
}

/**
 * Adds a calendar duration to a date, returning a new Date. Calendar-aware: month and year
 * arithmetic clamps correctly (Jan 31 + 1 month = Feb 28/29).
 *
 * @param value - The starting date.
 * @param duration - Temporal duration fields, e.g. `{ days: 1 }` or `{ months: -2 }`.
 * @returns A new Date shifted by the duration, or an invalid Date when the value is not a valid date.
 */
export function addToDate(value: Date | string | number, duration: Temporal.DurationLike): Date {
  const date = zoned(value);
  return date ? new Date(date.add(duration).epochMilliseconds) : invalidDate();
}

/**
 * Adjusts a given date to the local timezone by shifting the instant so its UTC fields
 * read as the original local wall clock.
 *
 * @param value - The date to adjust.
 * @returns A new shifted Date object.
 */
export function adjustToLocalTimezone(value: Date | string | number): Date {
  const date = new Date(value as string);
  return new Date(date.getTime() + date.getTimezoneOffset() * 60000);
}

/**
 * Formats the local time of day as "HH:mm".
 *
 * @param value - The date. Can be a Date object, a date string, or a timestamp.
 * @returns The zero-padded local time, e.g. "08:30", or "" when the value is not a valid date.
 */
export function maskTime(value: Date | string | number): string {
  const time = zoned(value);
  if (!time) return "";
  return `${String(time.hour).padStart(2, "0")}:${String(time.minute).padStart(2, "0")}`;
}

/**
 * Returns a new Date with the given local time-of-day fields replaced. Fields left
 * unspecified are preserved from the original.
 *
 * @param value - The date to modify.
 * @param time - The time fields to set, e.g. `{ hour: 8, minute: 30 }`.
 * @returns A new Date with the replaced time, or an invalid Date when the value is not a valid date.
 */
export function setTime(
  value: Date | string | number,
  time: { hour?: number; minute?: number; second?: number; millisecond?: number },
): Date {
  const date = zoned(value);
  return date ? new Date(date.with(time).epochMilliseconds) : invalidDate();
}

/**
 * Formats a date as "Segunda-feira, 09 de junho" (pt-BR weekday, padded day, month name).
 *
 * @param value - The date to format.
 * @returns The long weekday-and-date label, or "" when the value is not a valid date.
 */
export function maskWeekdayDate(value: Date | string | number): string {
  const date = new Date(value as string);
  const zonedDate = zoned(date);
  if (!zonedDate) return "";
  const weekday = date.toLocaleDateString("pt-BR", { weekday: "long" });
  const day = String(zonedDate.day).padStart(2, "0");
  const month = date.toLocaleDateString("pt-BR", { month: "long" });
  return `${weekday.charAt(0).toUpperCase()}${weekday.slice(1)}, ${day} de ${month}`;
}

/**
 * True when both dates fall on the same local calendar day. False when either is not a valid date.
 */
export function isSameDay(from: Date | string | number, to: Date | string | number): boolean {
  const [start, end] = [zoned(from), zoned(to)];
  if (!start || !end) return false;
  return start.toPlainDate().equals(end.toPlainDate());
}

/**
 * True when both dates fall in the same local calendar month (and year). False when either is not a valid date.
 */
export function isSameMonth(from: Date | string | number, to: Date | string | number): boolean {
  const [start, end] = [zoned(from), zoned(to)];
  if (!start || !end) return false;
  return start.toPlainDate().toPlainYearMonth().equals(end.toPlainDate().toPlainYearMonth());
}

/**
 * True when both dates fall in the same local calendar year. False when either is not a valid date.
 */
export function isSameYear(from: Date | string | number, to: Date | string | number): boolean {
  const [start, end] = [zoned(from), zoned(to)];
  if (!start || !end) return false;
  return start.year === end.year;
}

/**
 * True when both ends of the range fall on today's local calendar day. False when either is not a valid date.
 */
export function isRangeToday(from: Date | string | number, to: Date | string | number): boolean {
  const [start, end] = [zoned(from), zoned(to)];
  if (!start || !end) return false;
  const today = Temporal.Now.plainDateISO(localTimeZone);
  return start.toPlainDate().equals(today) && end.toPlainDate().equals(today);
}

/**
 * True when both ends of the range fall on tomorrow's local calendar day. False when either is not a valid date.
 */
export function isRangeTomorrow(from: Date | string | number, to: Date | string | number): boolean {
  const [start, end] = [zoned(from), zoned(to)];
  if (!start || !end) return false;
  const tomorrow = Temporal.Now.plainDateISO(localTimeZone).add({ days: 1 });
  return start.toPlainDate().equals(tomorrow) && end.toPlainDate().equals(tomorrow);
}

/**
 * True when both ends of the range fall on yesterday's local calendar day. False when either is not a valid date.
 */
export function isRangeYesterday(from: Date | string | number, to: Date | string | number): boolean {
  const [start, end] = [zoned(from), zoned(to)];
  if (!start || !end) return false;
  const yesterday = Temporal.Now.plainDateISO(localTimeZone).subtract({ days: 1 });
  return start.toPlainDate().equals(yesterday) && end.toPlainDate().equals(yesterday);
}

/**
 * Formats a date for UI display as a pt-BR date ("09/06/2026").
 *
 * @param value - The date to format.
 * @returns The formatted date, or the original value when it is not a valid date.
 */
export function maskDate(value: Date | string | number): string {
  if (!isValid(value)) return value as string;
  return new Date(value as string).toLocaleDateString("pt-BR", {
    day: "numeric",
    month: "numeric",
    year: "numeric",
  });
}

/**
 * Formats a date and time for UI display ("09/06/2026, 14:30:45").
 *
 * @param value - The date to format.
 * @returns The formatted date-time, or the original value when it is not a valid date.
 */
export function maskDateTime(value: Date | string | number): string {
  if (!isValid(value)) return value as string;
  return new Date(value as string).toLocaleString("pt-BR", {
    day: "numeric",
    month: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

/**
 * Formats a "YYYY-MM-DD" ISO date string as "DD/MM/YYYY" without any timezone conversion.
 *
 * @param value - The ISO date string.
 * @returns The formatted date, "—" when empty, or the original value when unparseable.
 */
export function maskISODate(value: string | null | undefined): string {
  if (!value) return "—";
  const parts = value.split("-");
  if (parts.length !== 3) return value;
  const [year, month, day] = parts;
  return `${day}/${month}/${year}`;
}
